import React, { useEffect, useState } from "react";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Slide,
  Snackbar,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GetAppOutlinedIcon from "@mui/icons-material/GetAppOutlined";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import AddToHomeScreenOutlinedIcon from "@mui/icons-material/AddToHomeScreenOutlined";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import { usePWA } from "../../hooks/usePWA";
import { APP_NAME, APP_TAGLINE, APP_ICON_192_SRC, MALAWI_BRAND } from "../../config/brand";

/** Thin Malawi flag–inspired accent bar (black · red · green). */
function MalawiAccentBar() {
  const { colors } = MALAWI_BRAND;
  return (
    <Box
      aria-hidden
      sx={{
        display: "flex",
        height: 4,
        width: "100%",
        borderRadius: "4px 4px 0 0",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, bgcolor: colors.black }} />
      <Box sx={{ flex: 1, bgcolor: colors.red }} />
      <Box sx={{ flex: 1, bgcolor: colors.green }} />
    </Box>
  );
}

function InstallStep({ step, icon: Icon, title, description }) {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Avatar
        sx={{
          width: 44,
          height: 44,
          bgcolor: alpha(MALAWI_BRAND.colors.sun, theme.palette.mode === "light" ? 0.35 : 0.2),
          color: theme.palette.mode === "light" ? MALAWI_BRAND.colors.black : MALAWI_BRAND.colors.sun,
          fontWeight: 700,
          fontSize: "1rem",
        }}
      >
        {Icon ? <Icon fontSize="small" /> : step}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
          {description}
        </Typography>
      </Box>
    </Stack>
  );
}

/** Android-only slide-up install card (Chromium beforeinstallprompt). */
function AndroidInstallPrompt({ visible }) {
  const theme = useTheme();
  const {
    installInstructions,
    promptInstall,
    dismissAndroidBanner,
  } = usePWA();
  const [installing, setInstalling] = useState(false);
  const [snack, setSnack] = useState("");

  const handleInstall = async () => {
    setInstalling(true);
    try {
      const { outcome } = await promptInstall();
      if (outcome === "dismissed") {
        setSnack("You can install anytime from App settings.");
      }
    } finally {
      setInstalling(false);
    }
  };

  return (
    <>
      <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
        <Paper
          className="pwa-install-prompt pwa-install-prompt--android"
          elevation={12}
          sx={{
            position: "fixed",
            bottom: { xs: "max(12px, env(safe-area-inset-bottom))", sm: 20 },
            left: { xs: 12, sm: "50%" },
            right: { xs: 12, sm: "auto" },
            transform: { sm: "translateX(-50%)" },
            width: { xs: "auto", sm: 440 },
            maxWidth: 440,
            zIndex: (t) => t.zIndex.snackbar,
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            bgcolor: "background.paper",
          }}
        >
          <MalawiAccentBar />
          <Box sx={{ position: "relative", px: 2, pt: 2, pb: 2 }}>
            <IconButton
              size="small"
              aria-label="Close install prompt"
              onClick={dismissAndroidBanner}
              sx={{ position: "absolute", top: 8, right: 8 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ pr: 4, mb: 2 }}>
              <Avatar
                src={APP_ICON_192_SRC}
                alt={APP_NAME}
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: "#fff",
                }}
              />
              <Box>
                <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle1" fontWeight={800} lineHeight={1.2}>
                    {installInstructions.title}
                  </Typography>
                  <Chip
                    size="small"
                    icon={<FlagOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                    label={MALAWI_BRAND.regionLabel}
                    sx={{
                      height: 22,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      bgcolor: alpha(MALAWI_BRAND.colors.sun, 0.25),
                      "& .MuiChip-icon": { ml: 0.5 },
                    }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {MALAWI_BRAND.tagline} · {APP_TAGLINE}
                </Typography>
              </Box>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
              Add {APP_NAME} to your home screen for quick access. Your school login and data stay exactly the same.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                fullWidth
                variant="contained"
                size="medium"
                disabled={installing}
                onClick={handleInstall}
                startIcon={<GetAppOutlinedIcon />}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  py: 1.1,
                  bgcolor: MALAWI_BRAND.colors.black,
                  color: "#fff",
                  "&:hover": { bgcolor: alpha(MALAWI_BRAND.colors.black, 0.88) },
                }}
              >
                {installInstructions.actionLabel || "Install app"}
              </Button>
              <Button
                fullWidth
                variant="text"
                size="medium"
                onClick={dismissAndroidBanner}
                sx={{ borderRadius: 2, fontWeight: 600, color: "text.secondary" }}
              >
                Maybe later
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Slide>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack("")}
        message={snack}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </>
  );
}

/** iOS Safari — modal with Share → Add to Home Screen steps. */
function IosInstallModal({ open }) {
  const theme = useTheme();
  const { installInstructions, dismissIosInstructions } = usePWA();

  const steps = [
    {
      icon: IosShareOutlinedIcon,
      title: "Tap Share",
      description: 'In Safari, tap the Share button (square with an arrow at the bottom of the screen).',
    },
    {
      icon: AddToHomeScreenOutlinedIcon,
      title: "Add to Home Screen",
      description: `Scroll the menu and choose "Add to Home Screen". Keep the name "${APP_NAME}" or edit it, then tap Add.`,
    },
    {
      icon: SmartphoneOutlinedIcon,
      title: "Open from your home screen",
      description: "Launch the app icon for fullscreen mode — same account, fees, and records as the website.",
    },
  ];

  return (
    <Dialog
      className="pwa-install-prompt pwa-install-prompt--ios"
      open={open}
      onClose={dismissIosInstructions}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          m: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        },
      }}
      aria-labelledby="ios-install-dialog-title"
    >
      <MalawiAccentBar />
      <DialogTitle id="ios-install-dialog-title" sx={{ pt: 2.5, pb: 1, pr: 6 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar
            sx={{
              width: 44,
              height: 44,
              bgcolor: alpha(MALAWI_BRAND.colors.red, 0.1),
              color: MALAWI_BRAND.colors.red,
            }}
          >
            <IosShareOutlinedIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} lineHeight={1.25}>
              {installInstructions.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {MALAWI_BRAND.tagline}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          aria-label="Close"
          onClick={dismissIosInstructions}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
          iPhone and iPad do not show an install button — add {APP_NAME} manually in Safari. Nothing installs without
          your confirmation.
        </Typography>
        <Stack spacing={2.5}>
          {steps.map((item, index) => (
            <InstallStep key={item.title} step={index + 1} {...item} />
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, flexDirection: "column", gap: 1 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={dismissIosInstructions}
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            py: 1.1,
            bgcolor: MALAWI_BRAND.colors.black,
            color: "#fff",
            "&:hover": { bgcolor: alpha(MALAWI_BRAND.colors.black, 0.88) },
          }}
        >
          Got it
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={dismissIosInstructions}
          sx={{ fontWeight: 600, color: "text.secondary" }}
        >
          Maybe later
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Non-intrusive install UX for the school management PWA.
 * - Android: bottom slide-up card (install + Maybe later)
 * - iOS: centered modal with step-by-step Safari instructions
 */
export default function InstallPrompt() {
  const { showAndroidInstallBanner, showIosInstallInstructions } = usePWA();
  const [androidReady, setAndroidReady] = useState(false);
  const [iosReady, setIosReady] = useState(false);

  // Brief delay so the prompt does not appear immediately on first paint.
  useEffect(() => {
    if (!showAndroidInstallBanner) {
      setAndroidReady(false);
      return undefined;
    }
    const t = setTimeout(() => setAndroidReady(true), 1800);
    return () => clearTimeout(t);
  }, [showAndroidInstallBanner]);

  useEffect(() => {
    if (!showIosInstallInstructions) {
      setIosReady(false);
      return undefined;
    }
    const t = setTimeout(() => setIosReady(true), 2200);
    return () => clearTimeout(t);
  }, [showIosInstallInstructions]);

  return (
    <>
      <AndroidInstallPrompt visible={showAndroidInstallBanner && androidReady} />
      <IosInstallModal open={showIosInstallInstructions && iosReady} />
    </>
  );
}
